import { MenuList, Tooltip } from "@mui/material";
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { escapeRegExp, getDateForDateObj, getIntOfStrDate, getStrDateOfDateObj } from "../MainPage/Tools/Tools";
import { IMaskInput } from "react-imask";
import DateCalendarEditor from "./EditorComponents/DateCalendarEditor";
import CalendarButton from "./EditorComponents/CalendarButton";
import UpDownButtons from "./EditorComponents/UpDownButtons";
import PickListEditor from "./EditorComponents/PickListEditor";
import ModalButton from "./EditorComponents/ModalButton";
import CheckBoxMultiCheck from "../MainPage/Sections/ElementsSections/CheckBoxMultiCheck";
import CheckboxList from "./EditorComponents/CheckBoxList";
import DetailsButton from "./EditorComponents/DetailsButton";
import CustomCheckBoxList from "./EditorComponents/CustomCheckBoxList";
import { colorList, setColor } from "./EditorComponents/ColorTools";
import EditorPopper from "./EditorComponents/EditorPopper";
import VirtualList, { EventDataInfo } from "../MainPage/Tools/VirtualList/VirtualList";
import listStyles from "../MainPage/Tools/VirtualList/virtuallist.module.css";
import editorStyles from "./EditorComponents/editorStyles.module.css";
import clsx from "clsx";
import { EditStyle } from "../MainPage/Tools/ProjectX";

// запрос для выпадающего списка
async function getParamValues(props: EditorProps, editor: any, checkList?: boolean) {
  // внешний запрос
  if (props.onDropDownList) {
    // SetLoadingCursor(true);
    const result = await props.onDropDownList(
      {
        ...props,
        module: props.type,
        events: props.onEvents,
        ID: props.id,
        editor: editor,
      },
      checkList,
    );
    // SetLoadingCursor(true, undefined, true);

    if (!result.length) {
      return [{ label: "Нет данных", id: "-1", disabled: true }];
    }

    return result;
  } else return [{ label: "Нет данных", id: "-1", disabled: true }];
}

// удаление разделителя из значения для редактора (он добавляется с помощью маски)
function fixValue(value?: string) {
  if (value !== undefined) {
    const val = value.match(/[0-9]/g);
    if (!val) return "";
    return val.join("");
  }
  return "";
}

/**
 * Заменяет символы-заполнители в значении маски на пробелы.
 * Замена выполняется только для символов, находящихся на позициях,
 * соответствующих символам ввода маски (`#`, `0`, `a`).
 * Символы, являющиеся литералами маски, не изменяются.
 * Например:
 * `value = "11.02.____"`, `mask = "##.##.####"` → `"11.02."`
 * @param value Значение поля, содержащее незаполненные позиции маски.
 * @param mask Маска поля ввода.
 * @param placeholder Символ, используемый для обозначения незаполненной позиции.
 * @returns Значение с заменёнными символами-заполнителями.
 */
function replaceMaskPlaceholders(value: string, mask: string, placeholder = "_"): string {
  let result = "";
  let maskIndex = 0;
  for (const char of value) {
    const maskChar = mask[maskIndex];
    const isInputChar = maskChar === "#" || maskChar === "0" || maskChar === "a";
    if (isInputChar && char === placeholder) {
      result += " ";
    } else result += char;
    maskIndex++;
  }
  return result.trim();
}

// регекс проверки правильности написания даты
const dateRegexp =
  /^(?:(?:31(\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\.)(?:0?[1,3-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/;

interface OnChangeData {
  valueChanged?: React.RefObject<boolean>;
  datatype?: string;
  addInfo?: any;
  type?: string;
  ObjRef?: string;
}

export interface EditListData {
  current?: string;
  fieldname?: string;
  record?: any;
  newValue: any;
}

export interface EditData {
  value?: string;
  tag?: string | number | null;
  input?: null | HTMLInputElement;
  proof?: boolean;
  textchanged?: string;
  checkList?: boolean;
  listClick?: true;
  listindex?: number;
  checked?: string;
  setValue?: React.Dispatch<React.SetStateAction<EventDataInfo>>;
  setInputValue?: React.Dispatch<React.SetStateAction<string>>;
  fixVal?: (value: string) => void;
}

export interface EditorDetails {
  [key: string]: any;
  GetDetails: {
    OnCloseUpListCustom: (data: EditData & EditListData) => void;
    OnCloseUpList: (data: EditData & EditListData) => void;
    OnEditCustom: (data: EditData) => void;
    OnEdit: (data: EditData) => void;
    OnListCustom: () => EventDataInfo[] | Promise<EventDataInfo[]>;
    OnList: () => EventDataInfo[] | Promise<EventDataInfo[]>;
  };
  AccDetails?: boolean;
  HardCodeReadOnlyForEditor?: boolean;
  Details?: boolean;
  ID?: string | number;
  updateRecord?: () => void;
  getText?: (id: string) => string;
}

export interface EditorProps {
  value?: string;
  objref?: string;
  path?: string;
  type?: string;
  datatype?: string;
  name?: string;
  record?: any;
  groupid?: string;
  fieldname?: string;
  current?: string;
  addInfo?: any;
  editMask?: string;
  editStyle?: string | number;
  openAfterRender?: number;
  readonly?: boolean;
  disabled?: boolean;
  HardCodeReadOnlyForEditor?: boolean;
  editor?: any;
  styleInput?: CSSProperties;
  placeholder?: string;
  placeholderVisible?: string;
  showHint?: string;
  selection?: string;
  background?: boolean;
  onlist?: string;
  listValues?: Array<EventDataInfo>;
  list?: string;
  grid?: boolean;
  details?: EditorDetails;
  hintText?: string;
  styleComponent?: CSSProperties;
  checked?: string;
  doubleClickOnInput?: boolean;
  id?: string;
  onEvents?: string;
  from?: "grid" | "Forms" | "ModalProgress";
  data?: any;
  editParam?: string;
  multisel?: boolean;
  settings?: { onEdit?: (data: any) => void };
  documentGrid?: boolean;
  docProps?: {
    Reference?: string;
    MasterID?: string;
    DocID?: string;
    ID: string | number;
    SectionID: string | number;
    DocCfgID: string | number;
  };
  title?: string;
  onEditCustomCheckList?: (data: any) => void | Promise<void>;
  func?: (data: any) => void | Promise<void>;
  onEditButtonClick?: (data: any) => void;
  onDropDownList?: (data: any, checkList?: boolean) => Promise<EventDataInfo[]> | EventDataInfo[];
  onChange?: (value: string, data: OnChangeData) => void;
  onEditList?: (data: EditData & EditListData) => void | Promise<void>;
  clickAway?: (
    ev: any,
    onEdit: (ev: any, value: any, textChamged: string) => void,
    blur: boolean,
  ) => void | Promise<void>;
  getItems?: () => Promise<Array<EventDataInfo>> | Array<EventDataInfo>;
  onListCustom?: () => Promise<Array<EventDataInfo>> | Array<EventDataInfo>;
  getEdition?: React.Dispatch<React.SetStateAction<any>>;
  onEdit?: (data: EditData & EditorProps) => void | Promise<void>;
}

/**
 * @param {Object} props - Параметры компонента.
 * @param {number|string} [props.editStyle=0] - Свойство определяющее какие кнопки будут отрисованы.
 * @param {string} [props.objref] - id элемента нужен для позиционирования в справочниках списках и тд.
 * @param {string} [props.value] - Входное значение инпута.
 * @param [props.onEdit] - Функция которая вызывается на любое редактирование.
 * @param [props.onDropDownList] - Функция вызывается на клик по кнопке списка(editStyle === 1) и должна возвращать список в виде [{ label: "название", id: "objref или другой идентификатор", disabled: true(опционально делает элемент недоступным в списке) }].
 * @param [props.onEditButtonClick] - Функция для клика на кнопку открытия диалогового окна.
 * @param [props.onEditList] - Функция вызывается при не дефолтном поведении на клик элемента выпадающего списка.
 * @param [props.onChange] - Функция вызывается при изменении значения inputValue.
 * @param [props.addInfo] - Добавочные данные для редактирования таблиц.
 * @param {string} [props.list] - Список в виде Elemen1\r\nElement2\r\n...(если список получается не по запросу, а от внешнего компонента).
 * @param {string} [props.placeholder] - Не стандартный заголовок.
 * @param [props.editor] - Объект в который запишется функция редактирования.
 * @param [props.getEdition] - Функция в который запишутся хуки управления состоянием значения инпута.
 * @param {boolean} [props.grid] - Отменяет обычное редактирование и создает отдельный слушатель событий на clickAway.
 * @param [props.clickAway] - Функция которая вызывается в отдельном слушателе событий при отмене стандартной.
 * @param {boolean} [props.disabled] - Свойство отключает полностью весь функционал редактора.
 * @param {boolean} [props.readOnly] - Отключает ввод с клавиатуры (кнопки работают).
 * @param [props.record] - Элемент в котором хранятся данные для внешнего редактирования(Например в параметрах).
 * @description
 * Общий принцип работы: Внешние данные и значение -> Editor -> редактирование(включая выбор из списка) -> Вызов функции onEdit(Новое значение и внешние данных переданные изначально)
 * -> редактирование через модальное окно -> Вызов функций onEditButtonClick(передача всех нужных данных, включая функцию onEdit) -> редактирование -> Вызов функции onEdit внутри компонента с модальным окном(Новое значение и внешние данных переданные изначально)
 * остальные параметры используются только для передачи в функции onEdit, onEditButtonClick, onDropDownList (при необходимости добавить новые, их нужно добавлять в ручную везде, где в них есть потребность).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export default function Editor(props: EditorProps) {
  const {
    value,
    HardCodeReadOnlyForEditor,
    addInfo,
    background,
    current,
    datatype,
    disabled,
    editMask,
    editor,
    fieldname,
    groupid,
    name,
    objref,
    path,
    readonly,
    record,
    showHint,
    placeholder,
    placeholderVisible,
    openAfterRender,
    type,
    selection,
    styleInput,
    editStyle = "0",
    onlist,
    listValues,
    list,
    grid,
    details,
    hintText,
    styleComponent,
    checked,
    doubleClickOnInput,
    id,
    onEditCustomCheckList,
    onChange,
    onEditList,
    clickAway,
    getItems,
    onListCustom,
    getEdition,
    onEdit,
  } = props;
  const EditStyle_PickList = (EditStyle.PickList & Number(editStyle)) !== 0; // Выпадающий список реализован
  const EditStyle_Calendar = (EditStyle.Calendar & Number(editStyle)) !== 0; // Календарь реализован
  const EditStyle_Check = (EditStyle.Check & Number(editStyle)) !== 0; // Чекбокс реализован
  const EditStyle_UpDown = (EditStyle.UpDown & Number(editStyle)) !== 0; // Инкремент и Декремент реализован
  const EditStyle_Ellipsis = (EditStyle.Ellipsis & Number(editStyle)) !== 0; // Кнопка открытия модального окна реализована
  const EditStyle_CheckList = (EditStyle.CheckList & Number(editStyle)) !== 0; // Список с множественным выбором реализован
  const EditStyle_ReadOnly = (EditStyle.ReadOnly & Number(editStyle)) !== 0; // Только для чтения(работает только для ввода с клавиатуры, кнопки будут работать штатно)
  const EditStyle_EditList = (EditStyle.EditList & Number(editStyle)) !== 0; // не реализовано
  const EditStyle_ColorBox = (EditStyle.ColorBox & Number(editStyle)) !== 0; // Список с цветами
  const EditStyle_AutoList = (EditStyle.AutoList & Number(editStyle)) !== 0; // не реализовано
  const EditStyle_Password = (EditStyle.Password & Number(editStyle)) !== 0; // не реализовано
  const EditStyle_CustomCheckList = editStyle === "customCheckList";
  const EditStyle_CustomColorList = editStyle === "customColorList";

  const autocompleteList = EditStyle_PickList || EditStyle_CustomColorList || EditStyle_ColorBox;

  const popover = autocompleteList || EditStyle_CheckList || EditStyle_CustomCheckList || EditStyle_Calendar;

  const modalButton = useRef<HTMLButtonElement>(null);
  const buttonCalendar = useRef<HTMLButtonElement>(null);
  const buttonPickList = useRef<HTMLButtonElement>(null);
  const buttonDetails = useRef<HTMLButtonElement>(null);

  const mainBlock = useRef<any>(null);
  const input = useRef<HTMLInputElement & any>(null);
  const maskRef = useRef(null);
  const autoCompleteValue = useRef<HTMLDivElement | any>(null);
  const placeholderRef = useRef("");
  const virtualList = useRef<null | HTMLDivElement>(null);
  const checkListObjRef = useRef<string | number | null>(null);

  const valueChanged = useRef<boolean>(false);
  const blurValue = useRef<boolean>(false);
  const filterDefaultValues = useRef<EventDataInfo[]>([{ label: "Нет данных", id: "-1", disabled: true }]);
  const focus = useRef<boolean>(false);

  const [styleInputState, setStyleInputState] = useState<CSSProperties>({});
  const [openTooltip, setOpenTooltip] = useState<boolean>(false);

  const [valueEditor, setValueEditor] = useState<EventDataInfo>({ label: "" });
  const [inputValue, setInputValue] = useState<string>("");

  const [selectionData, setSelectionData] = useState<string | undefined>(selection);

  const [paramValuesItems, setParamValues] = useState<EventDataInfo[]>([
    { label: "Нет данных", id: "-1", disabled: true },
  ]);
  const [openList, setOpenList] = useState<boolean>(false);
  const [height, setHeight] = useState<number>(0);
  const [filter, setFilter] = useState<string>("");

  const [anchorElDate, setAnchorElDate] = React.useState(null);
  const openDate = Boolean(anchorElDate);
  const idDate = openDate ? "calendar" : undefined;

  const [anchorElForCheckList, setAnchorElForCheckList] = React.useState(null);
  const [checkedList, setCheckedList] = React.useState<number[]>([]);
  const openForCheckList = Boolean(anchorElForCheckList);
  const idForCheckList = openForCheckList ? "checklist" : undefined;

  const initEditorValue = () => {
    if (!mainBlock.current) return;
    mainBlock.current.changeValue = changeInputValue;
    if (openAfterRender !== undefined) openEditorAfterRender(openAfterRender);
    const label = editMask && !EditStyle_Calendar ? fixValue(value) : value;
    setValueEditor({
      label: label,
      id: objref,
    });
    setInputValue(label || "");
  };
  useEffect(initEditorValue, []);

  const fixEmptyInput = () => {
    if (inputValue === undefined) setInputValue("");
  };
  useEffect(fixEmptyInput, [inputValue]);

  const updatePropsEditor = () => {
    initPlaceholder();
    focus.current = false;
    if (EditStyle_CheckList) {
      checkListObjRef.current = objref || "0";
    }
    if (editor) {
      editor.onEdit = onEditValue;
    }
    setStyleInputState({
      backgroundColor: background ? "whitesmoke" : "inherit",
      color: EditStyle_ReadOnly || readonly || disabled || HardCodeReadOnlyForEditor ? "#a8a6a6" : "#212529",
      ...styleInput,
    });
  };
  useEffect(updatePropsEditor, [props]);

  const updateValueEditor = () => {
    const label = editMask && !EditStyle_Calendar ? fixValue(value) : value;
    setValueEditor({
      label: label,
      id: objref,
    });
    setInputValue(label || "");
  };
  useEffect(updateValueEditor, [value, objref]);

  const outerEditing = () => {
    if (getEdition) {
      const editionData = {
        setValue: setValueEditor,
        setInputValue,
        value: valueEditor,
        inputValue,
        setSelection: setSelectionData,
        id: undefined,
        key: undefined,
      };
      if (record) {
        editionData.id = record.id;
        editionData.key = record.dataset.key;
      }
      getEdition(editionData);
    }
  };
  useEffect(outerEditing, [getEdition]);

  useEffect(() => {
    if (paramValuesItems && EditStyle_CheckList && checkedList.length === 0 && !paramValuesItems[0].disabled) {
      let objR = Number(objref || "0");

      setCheckedList(
        paramValuesItems.map((item) => {
          if (objR > 0 && objR & Number(item.id)) {
            objR -= Number(item.id);
            return Number(item.id);
          }
          return -1;
        }),
      );
    } else if (paramValuesItems && EditStyle_CheckList && valueEditor.id) {
      updateCheckList(valueEditor.id);
    }

    setHeight(paramValuesItems.length * 21 < 240 ? paramValuesItems.length * 21 + 4 : 244);
  }, [paramValuesItems, valueEditor]);

  useEffect(() => {
    if (!(EditStyle_ReadOnly || readonly)) return;
    if (filter !== "") {
      setParamValues(
        filterDefaultValues.current.filter((option) => {
          return option.label?.toLowerCase().match(filter.toLowerCase());
        }),
      );
      return;
    }
    if (filter === "") {
      setParamValues(filterDefaultValues.current);
    }
  }, [filter]);

  useEffect(() => {
    if (openList) {
      input.current?.focus();
    }
  }, [openList]);

  const initPlaceholder = () => {
    if (EditStyle_Check) {
      placeholderRef.current = "";
    } else if (editMask && !EditStyle_Calendar) {
      placeholderRef.current = editMask.replaceAll("#", "_");
      placeholderRef.current = editMask.replaceAll("0", "_");
    } else placeholderRef.current = placeholder !== undefined ? placeholder : "не выбрано";
  };

  const onOpenToolTip = () => {
    if (showHint) setOpenTooltip(true);
  };

  const onCloseToolTip = () => {
    if (showHint) setOpenTooltip(false);
  };

  const handleClickDate = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (EditStyle_Ellipsis) {
      modalButton.current?.click();
      return;
    }
    setAnchorElDate(event.currentTarget);
    if (EditStyle_PickList) setOpenList(false);
  };

  const handleCloseDate = () => {
    setAnchorElDate(null);
  };

  const handleClickForCheckList = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElForCheckList(event.currentTarget);
  };

  const handleCloseForCheckList = async () => {
    // valueChanged.current = true;
    setAnchorElForCheckList(null);

    if (EditStyle_CustomCheckList) {
      return;
    }
    await onEdit?.({
      record: record,
      tag: checkListObjRef.current,
      value: inputValue,
      textchanged: "0",
      setValue: setValueEditor,
      setInputValue: setInputValue,
      addInfo: addInfo,
      checkList: true,
    });
  };

  const handleToggle = (ev: React.MouseEvent<HTMLDivElement>, value: EventDataInfo) => {
    const currentIndex = checkedList.indexOf(Number(value.id));
    const indexItem = [...ev.target.closest("UL").children].indexOf(ev.target.closest("LI"));
    const newChecked = [...checkedList];

    if (currentIndex === -1) {
      newChecked[indexItem] = Number(value.id);
      checkListObjRef.current = Number(checkListObjRef.current) + Number(value.id);
    } else {
      newChecked[indexItem] = -1;
      checkListObjRef.current = Number(checkListObjRef.current) - Number(value.id);
    }

    setInputValue(getCheckListInputValue(newChecked));

    setCheckedList(newChecked);
  };

  const getCheckListInputValue = (newChecked: number[]) => {
    const result: string[] = [];
    paramValuesItems.forEach((item, index) => {
      if (newChecked[index] !== -1) {
        result.push(item.label || "");
      }
    });
    return result.join(",");
  };

  const setCustomColorList = async () => {
    if (onListCustom) {
      const list = await onListCustom();

      filterDefaultValues.current = list.map((item) => {
        return {
          label: item.Name || "",
          id: item.Mask,
          color: setColor(Number(item.Color)),
        };
      });

      setParamValues(filterDefaultValues.current);
    }

    setOpenList(!openList);
  };

  const setColorList = () => {
    filterDefaultValues.current = colorList;
    setParamValues(colorList);
    setOpenList(!openList);
  };

  const setCustomCheckList = async (ev: React.MouseEvent<HTMLButtonElement>) => {
    filterDefaultValues.current = (await getItems?.()) || [];
    setParamValues(filterDefaultValues.current);
    handleClickForCheckList(ev);
    setAnchorElForCheckList(ev.target);
  };

  const setCheckList = async (ev: React.MouseEvent<HTMLButtonElement>) => {
    // чеклист
    if (!onlist && listValues && listValues.length) {
      filterDefaultValues.current = listValues;
      setParamValues(listValues);
      handleClickForCheckList(ev);
      setAnchorElForCheckList(ev.target);
      return;
    }

    if (!onlist && list && list !== "") {
      const listForCheckList = list.split("\r\n");
      if (listForCheckList.length && listForCheckList[listForCheckList.length - 1] === "") listForCheckList.pop();
      filterDefaultValues.current = listForCheckList.map((item, index) => {
        return { label: item, id: Math.pow(2, index) };
      });
      setParamValues(filterDefaultValues.current);
    } else {
      filterDefaultValues.current = await getParamValues(props, autoCompleteValue.current, true);
      setParamValues(filterDefaultValues.current);
    }
    handleClickForCheckList(ev);
    setAnchorElForCheckList(ev.target);
  };

  const setPickList = async () => {
    // обычный список
    if (!onlist && listValues && listValues.length) {
      filterDefaultValues.current = listValues;
      setParamValues(listValues);
      setOpenList(!openList);
      return;
    }

    if (!onlist && list && list !== "") {
      const pickListData = list.split("\r\n");
      if (pickListData.length && pickListData[pickListData.length - 1] === "") pickListData.pop();
      filterDefaultValues.current = pickListData.map((label, id) => ({ label, id }));
      setParamValues(filterDefaultValues.current);
    } else {
      filterDefaultValues.current = await getParamValues(props, autoCompleteValue.current);
      setParamValues(filterDefaultValues.current);
    }
    setOpenList(!openList);
  };

  // функция для кнопки открытия выпадающего списка
  const onPickListClick = async (ev: React.MouseEvent<HTMLButtonElement>) => {
    if (EditStyle_CustomColorList) {
      setCustomColorList();
      return;
    }
    if (EditStyle_ColorBox) {
      setColorList();
      return;
    }
    if (EditStyle_CustomCheckList) {
      setCustomCheckList(ev);
      return;
    }
    // заполнение выпадающего списка данными
    if (EditStyle_CheckList) {
      setCheckList(ev);
      return;
    }

    if (EditStyle_PickList) setPickList();
  };

  const openEditorAfterRender = (command: number) => {
    if (modalButton.current || buttonPickList.current || buttonCalendar.current) {
      const mButton = modalButton.current;
      const lButton = (EditStyle_Calendar && buttonCalendar.current) || buttonPickList.current;
      const comands = [() => EditStyle_Ellipsis && mButton?.click(), () => popover && lButton?.click()];
      comands[command]?.();
    }
  };

  function updateCheckList(objref?: string | number) {
    if (objref === undefined) return;
    let id = Number(objref);
    setCheckedList(
      paramValuesItems.map((item) => {
        if (id > 0 && id & Number(item.id)) {
          id -= Number(item.id);
          return Number(item.id);
        }
        return -1;
      }),
    );
    checkListObjRef.current = objref;
  }

  /**
   * Форматирует строку с датой и возвращает ее в правильном формате для объекта Date.
   * Если дата указана частично - она автоматически дополняется частями сегодняшней даты.
   *
   * @param str строка с датой в формате DD.MM.YYYY
   * @returns Возвращает строку в формате для объекта Date - YYYY-MM-DD
   */
  function getDateInFormat(str: string) {
    if (str) {
      const date = getDateForDateObj(str);
      return date ? date : checkDateCompletion(str);
    }
  }

  // функция заполняет данные месяца и года, если они не были указаны
  function checkDateCompletion(date: string) {
    const now = new Date();
    const separator = date.includes(".") ? "." : "-";
    const value = date.split(separator).map((item) => item.replaceAll("_", ""));
    if (value.length === 3 && value.some((item) => item !== "")) {
      if (value[0].length === 4) [value[0], value[2]] = [value[2], value[0]];
      if (value[0] === "0") value[0] = "1";
      if (value[1] === "0") value[1] = "1";
      const currentYear = now.getFullYear().toString();
      const currentMonth = String(now.getMonth() + 1).padStart(2, "0");
      const year = currentYear.slice(0, 4 - value[2].length) + value[2];
      const month = value[1] ? value[1].padStart(2, "0") : currentMonth;
      const day = value[0] ? value[0].padStart(2, "0") : "01";
      return `${year}-${month}-${day}`;
    }
  }

  // функция для установки даты в формат для сервера
  // {label:DD.MM.YYYY, id:number}
  function getDateBack(value: string) {
    const date = getDateInFormat(value);

    if (date) {
      const id = getIntOfStrDate(date);
      return { label: value === ".." ? "" : getStrDateOfDateObj(new Date(date)), id: id };
    }
    return { label: "", id: "" };
  }

  // события на затемнение при наведении
  function onMouseEnter() {
    if (Boolean(disabled || HardCodeReadOnlyForEditor)) return;
    if (input.current) input.current.style.backgroundColor = "#fbfbfb";
  }
  function onMouseLeave() {
    if (Boolean(disabled || HardCodeReadOnlyForEditor)) return;
    if (input.current) input.current.style.backgroundColor = "";
    if (!focus.current) {
      if (placeholderVisible !== "1" && input.current) input.current.placeholder = "";
    }
  }

  // функция на фокус input
  function onFocus() {
    if (!input.current) return;
    input.current?.select();
    input.current.placeholder = placeholderRef.current;
    focus.current = true;
    // для таблиц отдельный слушатель событий
    if (grid) {
      document.addEventListener("click", clickAwayEditor);
      input.current.deleteClickAway = () => document.removeEventListener("click", clickAwayEditor);
    }
  }
  // функция на потерю фокуса input
  function onBlur(ev: React.FocusEvent<HTMLDivElement>) {
    if (openList) return;
    if (ev.target.tagName === "INPUT" || ev.target.tagName === "TEXTAREA") {
      if (EditStyle_Calendar || datatype === "date") setInputValue(ev.target.value);
      focus.current = false;
      if (autocompleteList) setOpenList(false);
      if (placeholderVisible !== "1") input.current.placeholder = "";
      // для таблиц отдельная логика редактирования
      if (!grid) {
        // вызов функции редактирования
        onEditValue(undefined, ev.target.value ? ev.target.value : inputValue, "1");
      }
      // значение на потерю фокуса для функции onEditValue
      blurValue.current = ev.target.value;
    }
  }

  // Слушатель событий для таблиц на потерю фокуса
  function clickAwayEditor(ev: any) {
    if (
      ev.target === autoCompleteValue.current?.closest("TD") ||
      ev.target.closest("[componenteditor]") ||
      (ev.target.closest(".background_ModalDialog") &&
        ev.target.closest(".background_ModalDialog") ===
          autoCompleteValue.current?.closest(".background_ModalDialog")) ||
      ev.target.closest("[checklist]") ||
      ev.target.closest("[calendar]") ||
      ev.target.closest("[details]") ||
      ev.target.closest("[loadmask]") ||
      ev.target.classList.contains("MuiBackdrop-root")
    ) {
      return;
    }

    // если клик произошел на любое место кроме выше перечисленных выполнится код ниже
    if (clickAway) {
      // внешняя функция редактирования
      clickAway?.(ev, onEditValue, blurValue.current);
    }

    document.removeEventListener("click", clickAwayEditor);
  }

  // Функция для запроса на редактирование
  function onEditValue(ev: any, newValue: any, textchanged?: string) {
    if (!onEdit || details) return;
    const editData: EditData = {
      ...props,
      input: input.current,
      proof: needProof(),
      textchanged: textchanged,
      setValue: setValueEditor,
      setInputValue: setInputValue,
      fixVal: editMask && !EditStyle_Calendar ? fixValue : undefined,
    };
    if (ev) {
      if (ev && (ev.key === "Enter" || ev.key === "NumpadEnter") && !grid) {
        if (typeof newValue === "object") {
          onEdit({
            ...editData,
            value: newValue.label,
            tag: newValue.id,
          });
          valueChanged.current = false;
          return;
        } else {
          const val = EditStyle_Calendar ? getDateBack(newValue) : { label: newValue, id: undefined };
          onEdit({
            ...editData,
            value: val.label,
            tag: val.id,
          });
        }
        valueChanged.current = false;
        return;
      }

      if (ev && (ev.key === "Enter" || ev.key === "NumpadEnter") && textchanged !== undefined) {
        if (typeof newValue === "object") {
          onEdit({
            ...editData,
            value: newValue.label,
            tag: newValue.id,
            proof: false,
          });
          valueChanged.current = false;
          return;
        }
      }

      if (ev.target && ev.target.tagName === "LI") {
        if (onEditList) {
          onEditList({
            current: current,
            tag: newValue.id,
            value: newValue.label,
            fieldname: fieldname,
            record: record,
            listClick: true,
            setValue: setValueEditor,
            setInputValue: setInputValue,
            newValue,
          });
          valueChanged.current = false;
          return;
        }
        onEdit({
          ...editData,
          listClick: true,
          proof: false,
          value: newValue.label,
          tag: newValue.objRef || newValue.id,
          listindex: ev.target.dataset.optionIndex,
          textchanged: "0",
        });
        valueChanged.current = false;
        return;
      }
    }

    if (newValue !== undefined) {
      if (!ev && valueChanged.current && needProof()) {
        const val = EditStyle_Calendar ? getDateBack(newValue) : { label: newValue, id: undefined };
        onEdit({
          ...editData,
          value: val.label,
          tag: val.id,
        });
        valueChanged.current = false;
        return;
      }

      if (!ev && valueChanged.current) {
        const val = EditStyle_Calendar
          ? getDateBack(newValue)
          : { label: editMask ? replaceMaskPlaceholders(newValue, editMask) : newValue, id: undefined };
        onEdit({
          ...editData,
          value: val.label,
          tag: val.id,
        });
        valueChanged.current = false;
        return;
      }
    }
  }

  // функция выбора маски по стилю и типу
  function getMask(dataType: string, editStyle: string) {
    if (editMask && !EditStyle_Calendar) {
      return editMask;
    }
    if (dataType === "2003") {
      return /^\d+[-]?[0-5]?[0-9]?$/;
    }
    if (dataType === "byte") {
      return /^[0]*[0-9]$|^[0]*[0-9][0-9]$|^[0]*1[0-9][0-9]$|^[0]*2[0-4][0-9]$|^[0]*25[0-5]$/;
    }
    if (dataType === "int" || dataType === "5" || dataType === "2" || dataType === "3") {
      return /^\d+$/;
    }
    if (dataType === "0" || dataType === "17") {
      return Number;
    }
    if (Number(editStyle) & 2 || dataType === "date") {
      return Date;
    }

    return /[.\n]*/;
  }

  // функция установки количества нулей после запятой
  function getScale(dataType: string) {
    if (dataType === "2003") return 2;
    if (dataType === "0") return 2;
    if (dataType === "17") return 3;
    return 0;
  }

  // функция установки дробного разделителя
  function getRadix(dataType: string, editStyle: string) {
    if (dataType === "2003") {
      return "-";
    }
    if (dataType === "0" || dataType === "17") {
      return ",";
    }
    if (Number(editStyle) & 2) {
      return ".";
    }
  }

  function getHint() {
    if (showHint) {
      if (hintText !== undefined && hintText !== "") {
        return hintText;
      } else {
        return input.current ? input.current.value : "";
      }
    }
    return "";
  }

  function needProof(): boolean {
    if (EditStyle_Calendar) return false;
    return Boolean((type || EditStyle_PickList || EditStyle_Ellipsis) && Number(datatype) >= 12);
  }

  function closeAllPopovers() {
    if (openList) {
      setOpenList(false);
    }
    if (anchorElForCheckList) {
      handleCloseForCheckList();
    }
    if (anchorElDate) {
      handleCloseDate();
    }
  }

  function onF4keyDown(ev: React.KeyboardEvent) {
    if (popover) {
      ev.stopPropagation();
      if (EditStyle_Calendar) {
        buttonCalendar.current?.click();
        return;
      }
      if (popover) {
        buttonPickList.current?.click();
        return;
      }
    }
  }

  const onKeyEnter = (ev: React.KeyboardEvent) => {
    if (openList || anchorElForCheckList || anchorElDate) {
      if (openList) {
        ev.stopPropagation();
        listSelect();
      }
      return;
    }
    if (ev.ctrlKey) {
      openEditorAfterRender(0);
      return;
    }
    valueChanged.current = true;
    input.current?.blur();
  };

  const listSelect = () => {
    const focused = virtualList.current?.querySelector<HTMLDivElement>(`.${clsx(listStyles.listItemFocus)}`);
    if (focused) {
      focused?.click();
      return;
    }

    const selected = virtualList.current?.querySelector<HTMLDivElement>(`.${clsx(listStyles.listItemSelected)}`);
    if (selected) {
      selected?.click();
      return;
    }
  };

  const onKeyEscape = () => {
    valueChanged.current = false;
    setInputValue(valueEditor.label || "");
    closeAllPopovers();
  };

  const onKeyS = (ev: React.KeyboardEvent) => {
    if (ev.ctrlKey) {
      ev.preventDefault();
      if (openList) {
        listSelect();
        return;
      }
      input.current?.blur();
    }
  };

  const onKeyArrow = (ev: React.KeyboardEvent) => {
    if (openList || anchorElForCheckList || anchorElDate) {
      if (ev.altKey) {
        closeAllPopovers();
        setTimeout(() => {
          input.current.focus();
        });
        return;
      }
      if (openList) {
        moveFocus(ev);
      }
      return;
    }
    if (ev.altKey && popover) {
      ev.stopPropagation();
      if (EditStyle_Calendar) {
        buttonCalendar.current?.click();
        return;
      }
      if (popover) {
        buttonPickList.current?.click();
        return;
      }
    }
  };

  const moveFocus = (ev: React.KeyboardEvent) => {
    ev.preventDefault();

    const focusItem = virtualList.current?.querySelector<HTMLDivElement>(`.${clsx(listStyles.listItemFocus)}`);
    if (focusItem) {
      const next =
        ev.code === "ArrowDown"
          ? (focusItem.nextElementSibling as HTMLDivElement | null)
          : (focusItem.previousElementSibling as HTMLDivElement | null);
      if (next) {
        focusItem.classList.remove(clsx(listStyles.listItemFocus));
        next.classList.add(clsx(listStyles.listItemFocus));
        next.focus();
        input.current.focus();
      }
      return;
    }

    const selected = virtualList.current?.querySelector<HTMLDivElement>(`.${clsx(listStyles.listItemSelected)}`);
    if (selected) {
      const next =
        ev.code === "ArrowDown"
          ? (selected.nextElementSibling as HTMLDivElement | null)
          : (selected.previousElementSibling as HTMLDivElement | null);
      if (next) {
        next.classList.add(clsx(listStyles.listItemFocus));
        next.focus();
        input.current.focus();
      }
      return;
    }

    const firstItem = virtualList.current?.firstChild as HTMLDivElement | null | undefined;
    if (firstItem) {
      const next =
        (ev.code === "ArrowDown" ? (firstItem.nextElementSibling as HTMLDivElement | null) : firstItem) || firstItem;
      next.classList.add(clsx(listStyles.listItemFocus));
      next.focus();
      input.current.focus();
    }

    return;
  };

  const onKeyF4 = (ev: React.KeyboardEvent) => {
    if (openList || anchorElForCheckList || anchorElDate) {
      closeAllPopovers();
    } else onF4keyDown(ev);
  };

  const onKeyDefault = (ev: React.KeyboardEvent) => {
    if (EditStyle_ReadOnly || readonly) {
      if (ev.key.length === 1) {
        setFilter(`${filter}${ev.key}`);
      } else if (ev.code === "Backspace") {
        setFilter(filter.slice(0, -1));
      }
    }
  };

  function onKeyDown(ev: React.KeyboardEvent) {
    if (openList) ev.stopPropagation();
    const keys: Partial<Record<string, (ev: React.KeyboardEvent) => void>> = {
      NumpadEnter: onKeyEnter,
      Enter: onKeyEnter,
      Escape: onKeyEscape,
      KeyS: onKeyS,
      ArrowUp: onKeyArrow,
      ArrowDown: onKeyArrow,
      F4: onKeyF4,
    };
    const handler = keys[ev.code];
    if (handler) {
      keys[ev.code]?.(ev);
    } else onKeyDefault(ev);
  }

  function changeInputValue(value: string) {
    valueChanged.current = true;

    onChange?.(value, {
      valueChanged: valueChanged,
      datatype: datatype,
      addInfo: addInfo,
      type: type,
      ObjRef: objref,
    });

    setInputValue(value);
  }

  return (
    <div
      ref={mainBlock}
      componenteditor="true"
      style={{
        height: "100%",
        width: "100%",
        ...styleComponent,
      }}
    >
      <div
        onFocus={(ev) => {
          if (!ev.target?.closest("[componenteditor]")) return;
          onFocus();
        }}
        onBlur={onBlur}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "row",
          paddingRight: "0px",
          paddingLeft: "1px",
          backgroundColor: "#ffffff",
        }}
      >
        {/* контейнер с кнопками перед input */}
        <div
          style={{
            display: "flex",
            height: "100%",
            alignItems: "center",
          }}
        >
          {/* чекбокс */}
          <CheckBoxMultiCheck
            onEdit={(data) => {
              onEdit?.({
                path: path,
                type: type,
                datatype: datatype,
                name: name,
                checked: data.CheckState,
                addInfo: addInfo,
              });
            }}
            parentProps={props}
            MultiCheckSet={undefined}
            CheckState={checked}
            // видимость
            display={EditStyle_Check ? "" : "none"}
            disabled={Boolean(disabled || HardCodeReadOnlyForEditor)}
          />
        </div>
        {/* маскированный input */}
        <Tooltip open={openTooltip} arrow placement="bottom-start" title={getHint()}>
          <div
            ref={autoCompleteValue}
            data-needproof={`${needProof()}`}
            style={{
              display: "flex",
              flex: "1 1 auto",
              alignItems: "center",
              ...styleComponent,
            }}
          >
            {Number(datatype) <= 12 && Number(datatype) >= 7 && !editMask ? (
              <div
                style={{
                  height: "100%",
                  width: "100%",
                }}
              >
                <textarea
                  disabled={Boolean(disabled || HardCodeReadOnlyForEditor)}
                  inputeditor="true"
                  placeholder={placeholderVisible === "1" ? placeholderRef.current : ""}
                  onMouseEnter={onOpenToolTip}
                  onMouseLeave={onCloseToolTip}
                  className={clsx(editorStyles.editorInput)}
                  style={{
                    ...styleInputState,
                    resize: "none",
                    height: "100%",
                    paddingTop: "3px",
                  }}
                  // type={"text"}
                  onDoubleClick={() => {
                    if (modalButton.current && modalButton.current.style.display !== "none" && doubleClickOnInput) {
                      modalButton.current.click();
                    }
                  }}
                  readOnly={EditStyle_ReadOnly || readonly}
                  value={inputValue ? inputValue : ""}
                  title={inputValue}
                  ref={(el) => {
                    input.current = el;
                  }}
                  onKeyDown={(ev) => {
                    if (ev.shiftKey) {
                      switch (ev.code) {
                        case "NumpadEnter":
                        case "Enter":
                          ev.stopPropagation();
                          break;
                        default:
                          onKeyDown(ev);
                          break;
                      }
                    }
                    return;
                  }}
                  // onChange для инпута
                  onChange={(ev) => {
                    if (inputValue !== ev.target.value) {
                      valueChanged.current = true;
                    }

                    onChange?.(ev.target.value, {
                      valueChanged: valueChanged,
                      datatype: datatype,
                      addInfo: addInfo,
                      type: type,
                      ObjRef: objref,
                    });
                    setInputValue(ev.target.value);
                  }}
                />
              </div>
            ) : (
              <IMaskInput
                disabled={Boolean(disabled || HardCodeReadOnlyForEditor)}
                inputeditor="true"
                placeholder={placeholderVisible === "1" && !editMask ? placeholderRef.current : undefined}
                onMouseEnter={onOpenToolTip}
                onMouseLeave={onCloseToolTip}
                onKeyDown={onKeyDown}
                className={clsx(editorStyles.editorInput)}
                style={styleInputState}
                type={"text"}
                onDoubleClick={() => {
                  if (modalButton.current && modalButton.current.style.display !== "none" && doubleClickOnInput) {
                    modalButton.current.click();
                  }
                }}
                readOnly={EditStyle_ReadOnly || readonly}
                // маска
                mask={getMask(datatype || "", editStyle)}
                definitions={{
                  a: /[A-Za-zА-Яа-я0-9]/,
                  "#": /\d/,
                }}
                // разделитель между тысячами
                thousandsSeparator=" "
                padFractionalZeros={true}
                scale={getScale(datatype || "")}
                radix={getRadix(datatype || "", editStyle)}
                // отображение маски в placeholder
                lazy={false}
                value={inputValue}
                title={inputValue}
                unmask={true}
                ref={maskRef}
                overwrite={EditStyle_Calendar}
                // проверка даты
                validate={(val) => {
                  if (!EditStyle_Calendar) return;
                  if (val.match(/[0-9]{2}.[0-9]{2}.[0-9]{4}/)) {
                    if (val.match(dateRegexp)) {
                      input.current.style.outline = "";
                      input.current.title = "";
                    } else {
                      input.current.style.outline = "1px solid red";
                      input.current.title = "Укажите корректную дату";
                    }
                  } else {
                    input.current.style.outline = "";
                    input.current.title = "";
                  }
                }}
                inputRef={(el) => {
                  input.current = el;
                }}
                // onChange для инпута
                onAccept={(maskValue, _mask, ev) => {
                  if ((inputValue === undefined || inputValue === "") && (maskValue === "" || maskValue === "..")) {
                    return;
                  }
                  if (inputValue !== maskValue) valueChanged.current = true;
                  onChange?.(maskValue, {
                    valueChanged: valueChanged,
                    datatype: datatype,
                    addInfo: addInfo,
                    type: type,
                    ObjRef: objref,
                  });
                  if (ev && ev.data === null) {
                    input.current.style.outline = "";
                    input.current.title = "";
                  }
                  if (EditStyle_Calendar || datatype === "date") {
                    setInputValue(input.current.value);
                    return;
                  }
                  setInputValue(maskValue);
                }}
              />
            )}
          </div>
        </Tooltip>

        {/* контейнер с кнопками после input */}
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {/* календарь */}
          <CalendarButton
            buttonRef={buttonCalendar}
            id={idDate}
            handleClick={handleClickDate}
            itemID={id}
            EditStyle_Calendar={EditStyle_Calendar}
            disabled={Boolean(disabled || HardCodeReadOnlyForEditor)}
          />
          {/* кнопки +- */}
          {EditStyle_UpDown && (
            <UpDownButtons
              inputRef={input}
              inputValue={inputValue}
              setInputValue={setInputValue}
              valueChanged={valueChanged}
              disabled={Boolean(disabled || HardCodeReadOnlyForEditor)}
            />
          )}
          {/* кнопка модального окна */}
          <ModalButton
            {...props}
            refButton={modalButton}
            EditStyle_Ellipsis={EditStyle_Ellipsis}
            EditStyle_Calendar={EditStyle_Calendar}
            inputValue={inputValue}
            valueEditor={valueEditor}
            input={input}
            selection={selectionData}
            setValue={setValueEditor}
            setInputValue={setInputValue}
            setSelection={setSelectionData}
            closeList={() => {
              if (autocompleteList) setOpenList(false);
            }}
            disabled={Boolean(disabled || HardCodeReadOnlyForEditor)}
          />
          {/* выпадающий список */}
          <PickListEditor
            buttonRef={buttonPickList}
            id={idForCheckList}
            itemId={id}
            EditStyle_PickList={Boolean(popover && !EditStyle_Calendar && !details)}
            onPickListClick={onPickListClick}
            disabled={Boolean(disabled || HardCodeReadOnlyForEditor)}
          />
          {details ? (
            <DetailsButton
              buttonRef={buttonDetails}
              autoCompleteValue={autoCompleteValue}
              details={details}
              inputValue={inputValue}
              module={type}
              setInputValue={setInputValue}
              setValue={setValueEditor}
              valueChanged={valueChanged}
            />
          ) : null}
          {EditStyle_CheckList && !EditStyle_CustomCheckList ? (
            <CheckboxList
              // objref={checkListObjRef.current}
              id={idForCheckList}
              open={openForCheckList}
              anchorEl={anchorElForCheckList}
              handleClose={handleCloseForCheckList}
              items={paramValuesItems}
              checked={checkedList}
              handleToggle={handleToggle}
            />
          ) : null}
          {EditStyle_CustomCheckList && !EditStyle_CheckList ? (
            <CustomCheckBoxList
              id={idForCheckList}
              open={openForCheckList}
              anchorEl={anchorElForCheckList}
              handleClose={handleCloseForCheckList}
              items={paramValuesItems}
              onEditCustomCheckList={onEditCustomCheckList}
            />
          ) : null}
        </div>
        {/* popover для выбора даты */}
        {EditStyle_Calendar ? (
          <DateCalendarEditor
            id={idDate}
            open={openDate}
            anchorEl={anchorElDate}
            handleClose={handleCloseDate}
            inputValue={inputValue}
            // для форм отключен авторассчет даты из-за проблем с метками
            objRef={props.from === "Forms" ? "0" : valueEditor.id}
            setInputValue={setInputValue}
            valueChanged={valueChanged}
            onEditValue={onEditValue}
          />
        ) : null}
        {popover && !EditStyle_Calendar && !details && openList ? (
          <EditorPopper
            open={openList}
            anchorElRef={input}
            onClose={(ev) => {
              if (ev.target?.closest(`[componenteditor="true"]`)) return;
              valueChanged.current = false;
              setInputValue(valueEditor.label || "");
              setOpenList(false);
            }}
            height={height}
          >
            <MenuList
              tabIndex="-1"
              style={{
                height: `${height}px`,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                padding: "0",
                paddingRight: "0px",
                paddingLeft: "3px",
                backgroundColor: "#ffffff",
              }}
            >
              <VirtualList
                refList={virtualList}
                noFocus
                elementToFocusRef={input}
                items={paramValuesItems.filter((item) => {
                  if (!valueChanged.current) return true;
                  return item.label?.toLowerCase()?.match(escapeRegExp(inputValue?.toLowerCase() || ""));
                })}
                selected={valueEditor}
                filterValue={inputValue}
                onClick={(data) => {
                  onEditValue(
                    {
                      target: { tagName: "LI", dataset: { optionIndex: data?.objRef === "0" ? data.id : data?.index } },
                    },
                    data,
                    "0",
                  );
                  setOpenList(false);
                }}
              />
            </MenuList>
          </EditorPopper>
        ) : null}
      </div>
    </div>
  );
}
